/**
 * Per-instance attraction attachment — the parent→child mapping carried on an
 * order-creation request.
 *
 * An ATTRACTION ticket cannot exist without a parent ENTRY ticket (enforced by
 * a DB trigger), but an order item only says "3× SLIDE_PASS" — it does not say
 * *which* of the entry tickets each pass belongs to. This structure supplies
 * that: `parentTemplateId` + `instanceIndex` identify one specific parent
 * instance, and `attractions[]` lists what was attached to it.
 *
 * `orders.service.ts` writes it to each ATTRACTION order item as
 * `metadata.attractionAttachment`, and the payment-confirmation saga reads it
 * back to resolve `Ticket.parentTicketId`. An ATTRACTION item that reaches the
 * saga without it aborts the whole fulfilment (#862).
 *
 * For `parentType: 'entry'`, `parentTemplateId` is the parent's **ticket type
 * id** — the saga buckets created ENTRY tickets by `ticketTypeId`, so anything
 * else fails to resolve. `instanceIndex` is the zero-based position among the
 * parents of that same type, in the order they appear in the request's
 * `items[]`, because that is the order the saga mints their tickets in.
 *
 * For `parentType: 'bundle'`, `parentTemplateId` is the **bundle id** and
 * `instanceIndex` selects which copy of that bundle. Since a bundle may now hold
 * several ENTRY lines, that pair no longer identifies a single parent — see
 * `BundleParentAddressingSchema` for the two fields that finish the address.
 *
 * Kept in its own leaf module (no imports back into the ticketing/kiosk
 * barrels) so the kiosk contracts can reference it without a circular import —
 * same reason as `kiosk/payment-method.ts`.
 */

import { z } from 'zod';

/**
 * Stable, bundle-scoped identifier for one line of a bundle's composition
 * (`BundleItem.itemKey`). Lower-case so it is unambiguous in URLs and JSON keys;
 * capped at 32 chars to match the column.
 */
export const BundleItemKeySchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_-]{0,31}$/, 'itemKey must match /^[a-z0-9][a-z0-9_-]{0,31}$/');

export const OrderAttractionAttachmentItemSchema = z.object({
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  quantity: z.number().int().positive(),
  slotId: z.string().optional(),
  /** WIB wall-clock HH:mm. Combined with the order's visitDate downstream. */
  slotStartTime: z.string().optional(),
  slotEndTime: z.string().optional(),
  /**
   * True when this row is a slot choice for an attraction the bundle ALREADY
   * includes, rather than a user-added add-on. It annotates a constituent row
   * the bundle produces on its own and must NOT become its own order item —
   * doing so charges the customer twice and mints two tickets for one
   * entitlement.
   */
  fromBundleInclusion: z.boolean().optional(),
  /**
   * Which bundle line the slot belongs to (`BundleItem.itemKey`), and which
   * visitor of the entry line that owns it. ticketTypeId cannot identify
   * either: a bundle may hold two lines of the same ticket type, and each
   * visitor picks their own time.
   */
  bundleItemKey: BundleItemKeySchema.optional(),
  parentEntryUnitIndex: z.number().int().nonnegative().optional(),
  /**
   * Which ENTRY line of the bundle this row belongs to. Item-level because one
   * attachment per bundle copy is all the cart allows, yet a copy admits
   * several visitors who may each choose different add-ons.
   */
  parentEntryItemKey: BundleItemKeySchema.optional(),
});

export type OrderAttractionAttachmentItem = z.infer<typeof OrderAttractionAttachmentItemSchema>;

/**
 * Which ENTRY line inside a bundle owns an add-on, and which unit of it.
 *
 * `instanceIndex` alone identifies *which copy of the bundle* — it cannot name
 * an entry line within that copy, which was unambiguous only while a bundle was
 * capped at one entry line. These two fields close that gap (#929).
 *
 * Both are optional so orders authored before multi-entry bundles still parse.
 * An omitted `parentEntryItemKey` means "the bundle's only ENTRY line"; if the
 * bundle has more than one, the saga raises rather than picking silently.
 */
export const BundleParentAddressingSchema = z.object({
  /** parentType 'bundle' only: the owning ENTRY line's `BundleItem.itemKey`. */
  parentEntryItemKey: BundleItemKeySchema.optional(),
  /** parentType 'bundle' only: which unit within that entry line (0-based). Omitted ⇒ 0. */
  parentEntryUnitIndex: z.number().int().nonnegative().optional(),
});

export const OrderAttractionAttachmentSchema = z
  .object({
    parentType: z.enum(['entry', 'bundle']),
    parentTemplateId: z.string(),
    instanceIndex: z.number().int().nonnegative(),
    instanceLabel: z.string(),
    attractions: z.array(OrderAttractionAttachmentItemSchema),
  })
  .merge(BundleParentAddressingSchema);

export type OrderAttractionAttachment = z.infer<typeof OrderAttractionAttachmentSchema>;
